import { css } from '@emotion/core'
import {
  ActionChip,
  Button,
  Divider,
  ResponsiveTableRow,
  Row,
  Separator,
  useOverlay,
  H5,
  Subheading,
} from '@fs/zion-ui'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveCheckboxTable } from '../DmcResponsiveTable'
import { deepCopy } from '../../../util/Utils'
import { MrnLink, PersonLink } from '../../app/Links'
import { PermissionsContext } from '../../app/PermissionsContext'
import { DotSeparator } from '../../metadata/MetaData'
import CmisDeleteButton from './CmisDeleteButton'
import CmisRefAddDialog from './CmisAddDialog'
import OwnerId from '../OwnerId'

const floatRight = css`
  float: right;
`

export default function CmisReference({
  index,
  personCopy,
  membershipSummaryMap,
  fetchCurrentPersonUser,
  totalSelectedEntityRefIds,
  setTotalSelectedEntityRefIds,
}) {
  const { t } = useTranslation()
  const addDialogOpener = useOverlay()
  const { isAuthorized } = useContext(PermissionsContext)
  const [selectedEntityRefIds, setSelectedEntityRefIds] = useState([])
  const possibleSelectedEntityRefIds = personCopy.cmisRefSummaries.map(
    (summary) => `${summary.entityRefId}_${personCopy.copyPersonId}`
  )
  useEffect(() => {
    let totalSelectedEntityRefIdsCopy = deepCopy(totalSelectedEntityRefIds)
    possibleSelectedEntityRefIds.forEach((id) => {
      if (selectedEntityRefIds.includes(id)) {
        if (!totalSelectedEntityRefIdsCopy.includes(id)) {
          totalSelectedEntityRefIdsCopy.push(id)
          setTotalSelectedEntityRefIds(totalSelectedEntityRefIdsCopy)
        }
      } else if (totalSelectedEntityRefIdsCopy.includes(id)) {
        totalSelectedEntityRefIdsCopy = totalSelectedEntityRefIdsCopy.filter(
          (idToRemoveCandidate) => id !== idToRemoveCandidate
        )
        setTotalSelectedEntityRefIds(totalSelectedEntityRefIdsCopy)
      }
    })
  }, [
    personCopy.copyPersonId,
    possibleSelectedEntityRefIds,
    selectedEntityRefIds,
    setTotalSelectedEntityRefIds,
    totalSelectedEntityRefIds,
  ])

  return (
    <>
      <Row>
        <H5 growX supplementary>
          <span style={{ marginInlineEnd: '.5rem' }}>{personCopy.copyPersonName}</span>
          {personCopy.tombstoned && (
            <span style={{ display: 'inline-block' }}>
              <ActionChip label={t('tombstoned', 'Tombstoned')} />
            </span>
          )}

          <Subheading>
            <span>
              <PersonLink personId={personCopy.copyPersonId} newTab />
            </span>
            <DotSeparator />
            <OwnerId ownerInfo={personCopy.ownerData} />
          </Subheading>
        </H5>
        {index === 0 && (
          <>
            <Row gutters="xxs">
              <Button
                emphasis="medium"
                onClick={addDialogOpener.handleClick}
                disabled={!isAuthorized('CmisxCreateAnyCmisReferencePermission')}
              >
                {t('person.references.addReference', 'Add reference')}
              </Button>
              <CmisDeleteButton refIds={totalSelectedEntityRefIds} fetchCurrentPersonUser={fetchCurrentPersonUser} />
            </Row>
            <CmisRefAddDialog onSubmit={fetchCurrentPersonUser} {...addDialogOpener} />
          </>
        )}
      </Row>
      <Divider />
      {personCopy.cmisRefSummaries && personCopy.cmisRefSummaries.length > 0 && (
        <>
          <DmcResponsiveCheckboxTable
            id={`cmisTable-${personCopy.copyPersonId}`}
            totalColumnCount={3}
            onSelectionChange={setSelectedEntityRefIds}
            atSizeColumns={{
              default: {
                labels: [t('mrn', 'MRN'), t('person.references.membershipName', 'Membership Name'), ''],
                columnWidths: [5, 5, 2],
              },
            }}
          >
            {personCopy.cmisRefSummaries.map((cmisRefSummary) => {
              const cmisId = cmisRefSummary.cmisId
              const mrn = membershipSummaryMap[cmisId].mrns
              const membershipName = membershipSummaryMap[cmisId].membershipName
              const entityRefId = cmisRefSummary.entityRefId
              return (
                <ResponsiveTableRow
                  key={`${entityRefId}${personCopy.copyPersonId}`}
                  name={`${entityRefId}_${personCopy.copyPersonId}`}
                  column1={<MrnLink mrn={mrn} />}
                  column2={membershipName}
                  column3={
                    <span css={floatRight}>
                      <CmisDeleteButton
                        refIds={`${entityRefId}_${personCopy.copyPersonId}`}
                        fetchCurrentPersonUser={fetchCurrentPersonUser}
                      />
                    </span>
                  }
                />
              )
            })}
          </DmcResponsiveCheckboxTable>
          <Separator size="nano" />
          <Divider />
          <Separator size="md" />
        </>
      )}
      {personCopy.cmisRefSummaries && personCopy.cmisRefSummaries.length < 1 && (
        <div>{t('person.references.noCmisReferencesFound', 'No CMIS references found')}</div>
      )}
    </>
  )
}
